import { useState, useEffect, useCallback } from "react";
import { HubConnectionBuilder, LogLevel, HubConnectionState } from "@microsoft/signalr";

/**
 * Hook personnalisé pour gérer la connexion SignalR
 * @param {string} signalRPath - URL du hub SignalR
 * @param {string} tenantId - ID du tenant
 * @returns {Object} - Connexion SignalR et son état
 */
export function useSignalRConnection(signalRPath, tenantId) {
  const [connection, setConnection] = useState(null);
  const [connectionState, setConnectionState] = useState("Disconnected");
  const [error, setError] = useState(null);

  // Créer et démarrer la connexion
  useEffect(() => {
    if (!signalRPath || !tenantId) return;

    const urlRoot = `${signalRPath}?tenant=t_${tenantId.replace(/-/g, "")}`;

    const newConnection = new HubConnectionBuilder()
      .withUrl(urlRoot)
      .configureLogging(LogLevel.Warning)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Stratégie de reconnexion avec backoff exponentiel
          if (retryContext.elapsedMilliseconds < 60000) {
            // Dans la première minute, essayer toutes les 2 secondes
            return 2000;
          } else if (retryContext.elapsedMilliseconds < 300000) {
            // Entre 1 et 5 minutes, essayer toutes les 10 secondes
            return 10000;
          } else {
            // Après 5 minutes, essayer toutes les 30 secondes
            return 30000;
          }
        }
      })
      .build();

    // Gestionnaires d'événements de connexion
    newConnection.onreconnecting((error) => {
      console.warn("SignalR reconnecting...", error);
      setConnectionState("Reconnecting");
      setError(error?.message || "Reconnecting...");
    });

    newConnection.onreconnected((connectionId) => {
      console.log("SignalR reconnected:", connectionId);
      setConnectionState("Connected");
      setError(null);
    });

    newConnection.onclose((error) => {
      console.error("SignalR connection closed:", error);
      setConnectionState("Disconnected");
      setError(error?.message || "Connection closed");
      
      // Tenter de reconnecter après 5 secondes
      setTimeout(() => {
        startConnection(newConnection);
      }, 5000);
    });

    // Fonction pour démarrer la connexion
    const startConnection = (conn) => {
      if (conn.state !== HubConnectionState.Disconnected) {
        return;
      }

      setConnectionState("Connecting");
      
      conn
        .start()
        .then(() => {
          console.log("SignalR Connected");
          setConnectionState("Connected");
          setConnection(conn);
          setError(null);
        })
        .catch((err) => {
          console.error("SignalR Connection Error:", err);
          setConnectionState("Disconnected");
          setError(err.message);
          
          // Réessayer après 5 secondes
          setTimeout(() => {
            startConnection(conn);
          }, 5000);
        });
    };

    // Démarrer la connexion initiale
    startConnection(newConnection);

    // Nettoyage : arrêter la connexion quand le composant est démonté
    return () => {
      if (newConnection) {
        newConnection
          .stop()
          .then(() => console.log("SignalR connection stopped"))
          .catch((err) => console.error("Error stopping SignalR connection:", err));
      }
    };
  }, [signalRPath, tenantId]);

  // Fonction pour invoquer une méthode sur le serveur
  const invoke = useCallback(async (methodName, ...args) => {
    if (!connection || connection.state !== HubConnectionState.Connected) {
      throw new Error("SignalR connection is not established");
    }

    try {
      return await connection.invoke(methodName, ...args);
    } catch (err) {
      console.error(`Error invoking ${methodName}:`, err);
      throw err;
    }
  }, [connection]);

  // Fonction pour s'abonner à un événement
  const subscribe = useCallback((eventName, handler) => {
    if (!connection) {
      console.warn("Cannot subscribe: SignalR connection not ready");
      return () => {};
    }

    connection.on(eventName, handler);

    // Retourner une fonction de nettoyage
    return () => {
      try {
        connection.off(eventName, handler);
      } catch (err) {
        console.error(`Error unsubscribing from ${eventName}:`, err);
      }
    };
  }, [connection]);

  // Fonction pour se désabonner d'un événement
  const unsubscribe = useCallback((eventName) => {
    if (connection) {
      try {
        connection.off(eventName);
      } catch (err) {
        console.error(`Error unsubscribing from ${eventName}:`, err);
      }
    }
  }, [connection]);

  return {
    connection,
    connectionState,
    error,
    isConnected: connectionState === "Connected",
    isConnecting: connectionState === "Connecting",
    isReconnecting: connectionState === "Reconnecting",
    invoke,
    subscribe,
    unsubscribe
  };
}
